import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type RouterService from '@ember/routing/router-service';
import type { Store as StoreService } from '@warp-drive/core';
import { saveRecord } from 'tio-data/requests/save-record';
import type TourManagerService from './tour-manager';
import type TourStorageService from './tour-storage';
import type ThoughtBubbleStorageService from './thought-bubble-storage';
import type FeaturesService from './features';
import type { TourConfig } from './tour-manager';
import type { ThoughtBubbleFlow, ThoughtBubbleApp } from '../types/thought-bubble';
import type { ThoughtBubbleFlowInvocation as ThoughtBubbleFlowInvocationModel } from 'tio-data/data/resources/thought-bubble-flow-invocation.type';
import { toDriverStep } from '../types/thought-bubble';
import type { SaveRecordOptions } from 'tio-data/requests/save-record';

/**
 * Invocation records are analytics: every write below is fire-and-forget and
 * its failure is swallowed into a `console.error`. `skipErrorReporting` keeps
 * those failures out of `TioResponseHandler`, which treats a 401 as
 * session-fatal and logs the user out — and a 401 here is routine, because
 * `trackFlowCompletion` runs from driver.js `onDestroyed`, which also fires
 * during teardown and logout, when the token is already stale or gone.
 */
const INVOCATION_SAVE_OPTIONS: SaveRecordOptions = {
  namespace: 'api/v2',
  skipErrorReporting: true,
};

/**
 * Runtime service for evaluating and triggering thought bubble flows.
 *
 * On each route transition, evaluates all active flows against:
 * - Current route (exact match or wildcard/glob pattern)
 * - Current domain (window.location.hostname)
 * - User targeting rules (company, employee attributes, first-login)
 * - Completion state (via tourStorage)
 *
 * Note: Role-based targeting is not yet implemented in this MVP.
 * The targeting system currently supports company/employee attribute rules
 * and first-login checks. Role support may be added in a future iteration.
 *
 * Delegates rendering to the existing TourManager + driver.js infrastructure.
 *
 * @memberof services
 */
export default class ThoughtBubbleRuntimeService extends Service {
  @service declare router: RouterService;
  @service declare store: StoreService;
  @service declare tourManager: TourManagerService;
  @service declare tourStorage: TourStorageService;
  @service declare thoughtBubbleStorage: ThoughtBubbleStorageService;
  @service declare features: FeaturesService;

  @tracked isInitialized = false;

  /**
   * Track flows dismissed during this session to avoid pestering users.
   */
  private dismissedThisSession = new Set<string>();

  /**
   * Bound handler for route change events.
   */
  private routeChangeHandler?: () => void;

  /**
   * Pending trigger timeout ID for cancellation on route changes.
   */
  private pendingTriggerTimeout?: ReturnType<typeof setTimeout>;

  /**
   * Pending animation frame ID for cancellation on route changes.
   */
  private pendingAnimationFrame?: number;

  /**
   * Pending queue timeout ID for sequential flow chaining.
   */
  private pendingQueueTimeout?: ReturnType<typeof setTimeout>;

  /**
   * Queue of matching flows to invoke sequentially on the current route.
   * When the current flow completes or is dismissed, the next one starts.
   */
  private flowQueue: ThoughtBubbleFlow[] = [];

  /**
   * Active invocation record ID for the currently running flow.
   * Used to PATCH step progress and completion status.
   */
  private activeInvocationId: string | null = null;

  /**
   * Whether the running flow ever reached its final step. Separates a genuine
   * completion from an abandonment, since `onDestroyed` fires for both.
   */
  private reachedFinalStep = false;

  /**
   * The user context object. Set during initialization by the consuming app.
   * Expected shape: { company?: { id: string }, roles: Array<{ relationshipType: string }>, ... }
   */
  private userContext: Record<string, unknown> | null = null;

  /**
   * Initialize the runtime service. Called after user context is available.
   * Subscribes to route transitions and evaluates flows on each change.
   *
   * @param userContext - An object with user/company/role info for targeting evaluation.
   */
  initialize(userContext: Record<string, unknown>) {
    if (this.isInitialized) return;

    this.userContext = userContext;
    this.isInitialized = true;

    this.routeChangeHandler = () => {
      this.evaluateAndTrigger();
    };
    this.router.on('routeDidChange', this.routeChangeHandler);

    // Also evaluate immediately for the current route
    this.evaluateAndTrigger();
  }

  /**
   * Tear down the service: unsubscribe from route events.
   */
  willDestroy() {
    super.willDestroy();
    if (this.routeChangeHandler) {
      this.router.off('routeDidChange', this.routeChangeHandler);
      this.routeChangeHandler = undefined;
    }
    if (this.pendingTriggerTimeout) {
      clearTimeout(this.pendingTriggerTimeout);
      this.pendingTriggerTimeout = undefined;
    }
    if (this.pendingAnimationFrame) {
      cancelAnimationFrame(this.pendingAnimationFrame);
      this.pendingAnimationFrame = undefined;
    }
    if (this.pendingQueueTimeout) {
      clearTimeout(this.pendingQueueTimeout);
      this.pendingQueueTimeout = undefined;
    }
    this.flowQueue = [];
  }

  // ─── Core Evaluation ────────────────────────────────────────────────────

  /**
   * Evaluate all active flows against the current context and trigger
   * matching flows in priority order (0 = highest). When multiple flows
   * match, they are queued and invoked sequentially.
   */
  private async evaluateAndTrigger() {
    try {
      // Don't trigger flows when the app is in visual builder mode
      if (new URLSearchParams(window.location.search).has('tb-builder')) return;

      if (this.pendingTriggerTimeout) {
        clearTimeout(this.pendingTriggerTimeout);
        this.pendingTriggerTimeout = undefined;
      }
      if (this.pendingAnimationFrame) {
        cancelAnimationFrame(this.pendingAnimationFrame);
        this.pendingAnimationFrame = undefined;
      }
      if (this.pendingQueueTimeout) {
        clearTimeout(this.pendingQueueTimeout);
        this.pendingQueueTimeout = undefined;
      }
      this.flowQueue = [];

      // Ensure flows are loaded from API (no-op for localStorage mode)
      await this.thoughtBubbleStorage.ensureLoaded();

      const currentRoute = this.router.currentRouteName ?? '';
      const currentUrl = this.router.currentURL ?? window.location.pathname;
      const currentPath = currentUrl.split('?')[0];

      const activeFlows = this.thoughtBubbleStorage.getActiveFlows();

      const matchingFlows = activeFlows.filter((flow) => {
        const reasons = {
          route: this.matchesRoute(flow.route, currentRoute, currentPath),
          app: this.matchesApp(flow.app),
          targeting: this.matchesTargetingRules(flow),
          notCompleted: !this.tourStorage.isTourCompleted(flow.id),
          notDismissed: !this.dismissedThisSession.has(flow.id),
        };
        const passed = Object.values(reasons).every(Boolean);
        if (!passed && this.isDebugEnabled()) {
          console.debug(`[thought-bubble] Flow "${flow.name}" skipped:`, reasons);
        }
        return passed;
      });

      if (matchingFlows.length === 0) return;

      // Sort by priority ascending (0 = highest priority)
      matchingFlows.sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));

      // Queue all matching flows and start the first one
      this.flowQueue = matchingFlows;
      this.triggerNextFlow();
    } catch (e) {
      console.error('Error evaluating thought bubble flows', e);
    }
  }

  /**
   * Trigger the next flow in the queue. Called after the current flow
   * completes or is dismissed.
   */
  private triggerNextFlow() {
    const flow = this.flowQueue.shift();
    if (!flow) return;
    this.triggerFlow(flow);
  }

  private triggerFlow(flow: ThoughtBubbleFlow) {
    this.activeInvocationId = null;
    this.reachedFinalStep = false;

    const sortedSteps = flow.steps.sort((a, b) => a.order - b.order);
    const totalSteps = sortedSteps.length;

    const tourConfig: TourConfig = {
      id: flow.id,
      name: flow.name,
      steps: sortedSteps.map(toDriverStep),
      driverOptions: {
        showProgress: totalSteps > 1,
        showButtons: totalSteps > 1 ? ['next', 'previous', 'close'] : ['next', 'close'],
        doneBtnText: totalSteps > 1 ? 'Done' : 'Got it',
        onHighlighted: (_element, _step, { state }) => {
          const stepIndex = state.activeIndex ?? 0;
          this.trackStepProgress(flow.id, stepIndex, totalSteps);
        },
        onDestroyed: () => {
          this.trackFlowCompletion();
          this.activeInvocationId = null;
          this.dismissedThisSession.add(flow.id);
          // After this flow completes or is dismissed, trigger the next queued flow
          if (this.flowQueue.length > 0) {
            this.pendingQueueTimeout = setTimeout(() => {
              this.pendingQueueTimeout = undefined;
              this.triggerNextFlow();
            }, 300);
          }
        },
      },
    };

    this.tourManager.registerTour(tourConfig);

    // Wait for the page to be fully painted and target elements to be present
    // before starting the tour. This prevents the popover from appearing before
    // the page layout is stable (initial load) and handles sub-tab transitions
    // where the route changes but the DOM hasn't updated yet.
    const firstSelector = sortedSteps[0]?.elementSelector;
    this.waitForReadyThenStart(flow.id, firstSelector);
  }

  /**
   * Wait for the next animation frame (paint complete), then poll for
   * the first step's target element before starting the tour.
   * Falls back to starting after a maximum wait time for no-element flows
   * or if the element never appears.
   */
  private waitForReadyThenStart(flowId: string, selector?: string) {
    // First, wait for the current frame to paint
    this.pendingAnimationFrame = requestAnimationFrame(() => {
      this.pendingAnimationFrame = undefined;

      // For flows without element selectors (centered popovers),
      // just wait a tick for the route's template to render
      if (!selector?.trim()) {
        this.pendingTriggerTimeout = setTimeout(() => {
          this.pendingTriggerTimeout = undefined;
          this.startTourSafely(flowId);
        }, 100);
        return;
      }

      // Poll for the target element with exponential backoff
      const maxWait = 5000;
      const startTime = Date.now();
      let delay = 100;

      const poll = () => {
        if (document.querySelector(selector)) {
          this.pendingTriggerTimeout = undefined;
          this.startTourSafely(flowId);
          return;
        }
        if (Date.now() - startTime > maxWait) {
          console.warn(
            `[thought-bubble] Gave up waiting for "${selector}" after ${maxWait}ms; starting flow "${flowId}" without target element`
          );
          this.pendingTriggerTimeout = undefined;
          this.startTourSafely(flowId);
          return;
        }
        delay = Math.min(delay * 1.5, 1000);
        this.pendingTriggerTimeout = setTimeout(poll, delay);
      };

      this.pendingTriggerTimeout = setTimeout(poll, delay);
    });
  }

  private startTourSafely(flowId: string) {
    try {
      this.tourManager.startTour(flowId);
    } catch (e) {
      console.error('Error starting thought bubble tour', e);
    }
  }

  // ─── Invocation Tracking ───────────────────────────────────────────────

  /**
   * Track step progress for a flow invocation.
   * On the first step (index 0), creates the invocation record if none is active.
   * On subsequent steps (or revisiting step 0), updates the steps_completed field.
   * All API calls are fire-and-forget.
   */
  private trackStepProgress(flowId: string, stepIndex: number, totalSteps: number) {
    if (stepIndex >= totalSteps - 1) {
      this.reachedFinalStep = true;
    }
    if (stepIndex === 0 && !this.activeInvocationId) {
      this.createInvocationRecord(flowId, totalSteps);
    } else {
      this.updateStepProgress(stepIndex, totalSteps);
    }
  }

  /**
   * Create a new invocation record when a flow starts (step 0).
   * Sets activeInvocationId immediately so step updates can be tracked
   * even while the initial POST is in-flight.
   */
  private async createInvocationRecord(flowId: string, totalSteps: number) {
    const invocationNonce = `${flowId}-${Date.now()}`;
    // Set immediately so subsequent onHighlighted calls can update progress
    // before the save resolves, and so we don't create duplicates on step 0.
    this.activeInvocationId = invocationNonce;

    try {
      const record = this.store.createRecord('thought-bubble-flow-invocation', {
        flowId,
        stepsCompleted: { 0: true, total: totalSteps },
        completed: false,
        browserMeta: this.getBrowserMeta(),
      }) as ThoughtBubbleFlowInvocationModel;
      await saveRecord(this.store, record, INVOCATION_SAVE_OPTIONS);
      // Only update to the server-assigned ID if this is still the active invocation.
      // Prevents a stale in-flight save from overwriting a newer invocation.
      if (this.activeInvocationId === invocationNonce) {
        this.activeInvocationId = record.id;
      }
    } catch (e) {
      // If save failed and this is still the active nonce, clear it so
      // subsequent step updates don't try to PATCH a non-existent record.
      if (this.activeInvocationId === invocationNonce) {
        this.activeInvocationId = null;
      }
      console.error('[thought-bubble] Failed to create invocation record', e);
    }
  }

  /**
   * Update step progress on the active invocation record.
   */
  private async updateStepProgress(stepIndex: number, totalSteps: number) {
    if (!this.activeInvocationId) return;
    try {
      const record = this.store.peekRecord(
        'thought-bubble-flow-invocation',
        this.activeInvocationId
      ) as ThoughtBubbleFlowInvocationModel | null;
      if (!record) return;
      record.stepsCompleted = {
        ...record.stepsCompleted,
        [stepIndex]: true,
        total: totalSteps,
      };
      await saveRecord(this.store, record, INVOCATION_SAVE_OPTIONS);
    } catch (e) {
      console.error('[thought-bubble] Failed to update step progress', e);
    }
  }

  /**
   * Mark the active invocation as completed.
   */
  private async trackFlowCompletion() {
    if (!this.activeInvocationId) return;
    // `onDestroyed` fires on dismissal and on route/page teardown as well as on
    // finishing, so without this every close would be recorded as a completion.
    if (!this.reachedFinalStep) return;
    try {
      const record = this.store.peekRecord(
        'thought-bubble-flow-invocation',
        this.activeInvocationId
      ) as ThoughtBubbleFlowInvocationModel | null;
      if (!record) return;
      record.completed = true;
      await saveRecord(this.store, record, INVOCATION_SAVE_OPTIONS);
    } catch (e) {
      console.error('[thought-bubble] Failed to mark invocation completed', e);
    }
  }

  /**
   * Collect browser metadata for the invocation record.
   */
  private getBrowserMeta(): Record<string, string> {
    const ua = navigator.userAgent;
    return {
      device_type: /Mobi|Android/i.test(ua) ? 'mobile' : 'desktop',
      os: this.detectOS(ua),
      browser_name: this.detectBrowser(ua),
      browser_version: this.detectBrowserVersion(ua),
    };
  }

  private detectOS(ua: string): string {
    if (ua.includes('Windows')) return 'Windows';
    if (ua.includes('Android')) return 'Android';
    if (/iPhone|iPad|iPod/.test(ua)) return 'iOS';
    if (ua.includes('Mac OS')) return 'macOS';
    if (ua.includes('Linux')) return 'Linux';
    return 'Unknown';
  }

  private detectBrowser(ua: string): string {
    if (ua.includes('Edg/')) return 'Edge';
    if (ua.includes('Chrome') && !ua.includes('Edg/')) return 'Chrome';
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Safari';
    return 'Unknown';
  }

  private detectBrowserVersion(ua: string): string {
    const patterns: [string, RegExp][] = [
      ['Edge', /Edg\/(\d+[\d.]*)/],
      ['Chrome', /Chrome\/(\d+[\d.]*)/],
      ['Firefox', /Firefox\/(\d+[\d.]*)/],
      ['Safari', /Version\/(\d+[\d.]*)/],
    ];
    for (const [, regex] of patterns) {
      const match = ua.match(regex);
      if (match?.[1]) return match[1];
    }
    return 'Unknown';
  }

  // ─── Route Matching ─────────────────────────────────────────────────────

  /**
   * Matches URL paths beginning with / against currentPath; other patterns match Ember route names.
   * * matches one segment and ** matches any depth.
   */
  matchesRoute(pattern: string, currentRoute: string, currentPath?: string) {
    if (!pattern) return false;

    const isUrlPattern = pattern.startsWith('/');

    if (isUrlPattern) {
      // Match against URL path
      const target = currentPath ?? window.location.pathname;
      return this.matchPattern(pattern, target, '/');
    }

    // Match against Ember route name
    return this.matchPattern(pattern, currentRoute, '.');
  }

  /**
   * Generic pattern matching with a configurable separator.
   * Supports exact match, single wildcard (*), and globstar (**).
   */
  private matchPattern(pattern: string, value: string, separator: string) {
    // Normalize: strip trailing separator
    const normPattern = pattern.endsWith(separator) ? pattern.slice(0, -separator.length) : pattern;
    const normValue = value.endsWith(separator) ? value.slice(0, -separator.length) : value;

    // Exact match
    if (normPattern === normValue) return true;

    // Build regex: escape the separator, then handle wildcards
    const escapedSep = separator === '.' ? '\\.' : '\\/';
    const segmentChar = separator === '.' ? '[^.]+' : '[^/]+';

    const regexStr = normPattern
      .replace(separator === '.' ? /\./g : /\//g, escapedSep)
      .replace(/\*\*/g, '__GLOBSTAR__')
      .replace(/\*/g, segmentChar)
      .replace(new RegExp(`${escapedSep}__GLOBSTAR__`, 'g'), `(?:${escapedSep}.*)?`)
      .replace(/__GLOBSTAR__/g, '.*');

    try {
      const regex = new RegExp(`^${regexStr}$`);
      return regex.test(normValue);
    } catch {
      return false;
    }
  }

  // ─── App Matching ───────────────────────────────────────────────────────

  matchesApp(flowApp: ThoughtBubbleApp) {
    switch (flowApp) {
      case 'admin':
        return this.features.isAdminApp;
      case 'employee':
        return this.features.isEmployeeApp;
    }
  }

  // ─── Targeting Rules ────────────────────────────────────────────────────

  /**
   * Evaluate targeting rules against the current user context.
   */
  matchesTargetingRules(flow: ThoughtBubbleFlow) {
    if (!this.userContext) return true;

    const { rules, matchMode, firstLoginOnly } = flow.targetingRules;
    const checks: boolean[] = [];

    // Evaluate each attribute rule
    for (const rule of rules) {
      const actual = this.getAttributeValue(rule.source, rule.attribute);
      const match = actual !== undefined && String(actual) === rule.value;
      checks.push(match);
    }

    // First login check
    if (firstLoginOnly) {
      checks.push(this.isFirstLogin());
    }

    // If no rules configured, allow all
    if (checks.length === 0) return true;

    const mode = matchMode || 'all';
    return mode === 'all' ? checks.every(Boolean) : checks.some(Boolean);
  }

  /**
   * Resolve an attribute value from the user context by source + attribute name.
   */
  private getAttributeValue(source: 'company' | 'employee', attribute: string): unknown {
    if (source === 'company') {
      const company = this.userContext?.['company'] as Record<string, unknown> | undefined;
      return company?.[attribute];
    }
    const employee = this.userContext?.['employee'] as Record<string, unknown> | undefined;
    return employee?.[attribute];
  }

  // ─── User Context Helpers ───────────────────────────────────────────────

  private isFirstLogin(): boolean {
    const logins = this.userContext?.['logins'] as Array<unknown> | undefined;
    // If login history is not available, we cannot safely determine first login
    if (!Array.isArray(logins)) {
      return false;
    }
    return logins.length <= 1;
  }

  /**
   * Check if debug logging is enabled via sessionStorage flag.
   * Set `sessionStorage.setItem('tb-debug', '1')` to enable.
   */
  private isDebugEnabled(): boolean {
    try {
      return sessionStorage.getItem('tb-debug') === '1';
    } catch {
      return false;
    }
  }

  // ─── Public API ─────────────────────────────────────────────────────────

  /**
   * Manually dismiss a flow for this session.
   */
  dismissFlow(flowId: string) {
    this.dismissedThisSession.add(flowId);
  }

  /**
   * Reset all session dismissals (useful for testing).
   */
  resetDismissals() {
    this.dismissedThisSession.clear();
  }

  /**
   * Force-show a specific flow regardless of targeting/completion state.
   * Useful for preview/testing in the admin UI.
   */
  forceShowFlow(flowId: string) {
    const flow = this.thoughtBubbleStorage.getFlow(flowId);
    if (!flow) {
      console.warn(`Thought bubble flow not found: ${flowId}`);
      return;
    }
    this.triggerFlow(flow);
  }
}

declare module '@ember/service' {
  interface Registry {
    'thought-bubble-runtime': ThoughtBubbleRuntimeService;
  }
}
